import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

// Clonotype definition options, should be kept in sync with backend API
export enum ClonotypeDefinition {
  HILARY = 'hilary', // used as default
  CDR3_LENGTH = 'cdr3_length',
  CDR3_IDENTITY = 'cdr3_identity',
  CDR3_ONLY = 'cdr3_only',
}

// Utility function to get display UI labels for clonotype definition
export function getClonotypeDefinitionLabel(method: ClonotypeDefinition | string): string {
  switch (method) {
    case ClonotypeDefinition.HILARY:
    case 'hilary':
      return 'HILARy';
    case ClonotypeDefinition.CDR3_ONLY:
    case 'cdr3_only':
      return 'CDR3 Identity';
    case ClonotypeDefinition.CDR3_LENGTH:
    case 'cdr3_length':
      return 'V/J + CDR3 Length';
    case ClonotypeDefinition.CDR3_IDENTITY:
    case 'cdr3_identity':
      return 'V/J + CDR3 Identity';
    default:
      return 'Unknown';
  }
}

// Utility function to get display UI descriptions for clonotype definition
export function getClonotypeDefinitionTooltip(method: ClonotypeDefinition | string): string {
  switch (method) {
    case ClonotypeDefinition.HILARY:
    case 'hilary':
      return 'HILARy: Recommended HILARy statistical model';
    case ClonotypeDefinition.CDR3_ONLY:
    case 'cdr3_only':
      return 'CDR3 Identity: Similar CDR3 sequences, based on similarity threshold, ignoring V and J gene usage';
    case ClonotypeDefinition.CDR3_LENGTH:
    case 'cdr3_length':
      return 'V/J + CDR3 Length: Identical V and J genes, and CDR3 sequences of the same length';
    case ClonotypeDefinition.CDR3_IDENTITY:
    case 'cdr3_identity':
      return 'V/J + CDR3 Identity: Identical V and J genes, and similar CDR3 sequences';
    default:
      return '';
  }
}

export interface PreprocessingState {
  [experimentId: string]: {
    clonotypeDefinition: ClonotypeDefinition;
  };
}

const initialState: PreprocessingState = {};

const preprocessingSlice = createSlice({
  name: 'preprocessing',
  initialState,
  reducers: {
    setClonotypeDefinition(state, action: PayloadAction<{ experimentId: string; clonotypeDefinition: ClonotypeDefinition }>) {
      const { experimentId, clonotypeDefinition } = action.payload;
      if (!state[experimentId]) {
        state[experimentId] = { clonotypeDefinition: ClonotypeDefinition.HILARY };
      }
      state[experimentId].clonotypeDefinition = clonotypeDefinition;
    },
  },
});

export const { setClonotypeDefinition } = preprocessingSlice.actions;
export default preprocessingSlice.reducer;
